import { type FC, type JSX, type PropsWithChildren, useCallback, useEffect } from 'react'
import {
  Form,
  Formik,
  type FormikConfig,
  type FormikProps,
  type FormikValues,
  isFunction,
  useFormikContext,
} from 'formik'
import { useWizardContext } from '~/setup/model/context.js'

type Props<Values extends FormikValues = FormikValues> = FormikConfig<Values> & {}

const InnerForm: FC<PropsWithChildren> = ({ children }) => {
  const { registerStepHandler: handleStep } = useWizardContext()
  const { submitForm, isValid } = useFormikContext()

  const submitHandler = useCallback(async () => {
    await submitForm()

    if (!isValid) {
      throw new Error('validation failed')
    }
  }, [isValid, submitForm])

  const handleKeyDown = (keyEvent: React.KeyboardEvent) => {
    const tagName = (keyEvent.target as HTMLElement)?.tagName?.toLowerCase()

    // Prevent this form level keydown handler from blocking Enter keys in textareas
    if (tagName === 'textarea') {
      return
    }

    if (keyEvent.code === 'Enter') {
      keyEvent.preventDefault()
    }
  }

  useEffect(() => handleStep(submitHandler), [handleStep, submitHandler])

  return (
    <Form noValidate onKeyDown={handleKeyDown}>
      {children}
    </Form>
  )
}

export function StepForm<Values extends FormikValues = FormikValues>({
  children,
  validateOnMount = true,
  validateOnChange = false,
  ...rest
}: Props<PropsWithChildren<Values>>): JSX.Element {
  return (
    <Formik validateOnChange={validateOnChange} validateOnMount={validateOnMount} {...rest}>
      {(formikProps) => (
        <InnerForm>
          {isFunction(children)
            ? (children as (bag: FormikProps<Values>) => React.ReactNode)(formikProps as FormikProps<Values>)
            : children}
        </InnerForm>
      )}
    </Formik>
  )
}
